import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import Cropper from "react-easy-crop";

import { deleteWorkspaceLogo, updateWorkspaceLogo } from "../../api/workspaceSettings";
import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import { useCurrentWorkspaceStore } from "../../store/useCurrentWorkspaceStore";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cropImage, getImageDimensions, readFileAsDataURL } from "@/utils/imageCropper";

interface WorkspaceLogoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  logoUrl?: string;
  workspaceName: string;
}

export const WorkspaceLogoDialog = ({ isOpen, onClose, logoUrl }: WorkspaceLogoDialogProps) => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { setSettings, refetch } = useWorkspaceSettingsStore();
  const { updateWorkspaceLogo: updateCurrentWorkspaceLogo } = useCurrentWorkspaceStore();
  const { updateWorkspaceLogo: updateWorkspaceInfoLogo } = useWorkspaceInformationStore();

  // Cropping states
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Drag and drop states
  const [isDragging, setIsDragging] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];

    if (file && file.type.startsWith("image/")) {
      processFile(file);
    } else {
      toast.error("Please drop a valid image file");
    }
  };

  const processFile = async (file: File) => {
    try {
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size must be less than 2MB");

        return;
      }

      // Validate file type
      if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
        toast.error("File must be JPEG, PNG, GIF, or WebP format");

        return;
      }

      // Validate file dimensions
      const dimensions = await getImageDimensions(file);

      if (dimensions.width < 50 || dimensions.height < 50) {
        toast.error("Image dimensions are too small. Minimum size is 50x50px");

        return;
      }

      // Read file and set for cropping
      const dataUrl = await readFileAsDataURL(file);

      setSelectedImage(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      toast.error("Failed to load image. Please try a different file.");
    }
  };

  const openFileDialog = () => {
    const input = document.createElement("input");

    input.type = "file";
    input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/webp";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (file) processFile(file);
    };
    input.click();
  };

  const handleUpload = async () => {
    if (!selectedImage || !croppedAreaPixels || !workspaceId) return;

    setIsUploading(true);
    try {
      // Crop the image
      const croppedFile = await cropImage(selectedImage, croppedAreaPixels, {
        maxSizeMB: 2,
        minDimensions: 50,
        maxDimensions: 2048,
        quality: 0.9,
      });

      const response = await updateWorkspaceLogo(workspaceId, croppedFile);

      // Update stores
      const { settings } = useWorkspaceSettingsStore.getState();

      if (settings) {
        setSettings({
          ...settings,
          workspace: {
            ...settings.workspace,
            picture: response.data.logo_url,
          },
        });
      }

      updateCurrentWorkspaceLogo(response.data.logo_url);
      updateWorkspaceInfoLogo(response.data.logo_url);

      toast.success(t("settings.workspaceInfo.logoUpdated"));

      // Reset cropping state
      setSelectedImage("");
      setCroppedAreaPixels(null);

      await refetch(workspaceId);
      onClose();
    } catch (error: any) {
      if (error.message?.includes("file size")) {
        toast.error("Cropped image is too large. Please try with a smaller image.");
      } else {
        const errorMessage =
          error?.response?.data?.message || t("settings.workspaceInfo.logoUpdateFailed");

        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!workspaceId) return;

    setIsDeleting(true);
    try {
      await deleteWorkspaceLogo(workspaceId);

      // Refetch workspace settings first to get the updated logo URL
      await refetch(workspaceId);

      const { settings } = useWorkspaceSettingsStore.getState();
      const newLogoUrl = settings?.workspace?.picture || "";

      if (settings) {
        setSettings({
          ...settings,
          workspace: {
            ...settings.workspace,
            picture: newLogoUrl,
          },
        });
      }

      // Update the current workspace store with the new logo (could be placeholder)
      updateCurrentWorkspaceLogo(newLogoUrl);
      updateWorkspaceInfoLogo(newLogoUrl);

      toast.success(t("settings.workspaceInfo.logoDeleted"));

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || t("settings.workspaceInfo.logoDeleteFailed");

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setSelectedImage("");
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsDragging(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-background/95 border-border/50 h-fit max-h-dvh max-w-3xl p-0 shadow-2xl backdrop-blur-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden rounded-xl opacity-10">
          <div className="absolute -top-20 -right-24 h-48 w-48 rounded-full bg-gradient-to-br from-blue-500 to-purple-500" />
          <div className="absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500" />
        </div>

        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="relative p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-6">
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto mb-4 flex size-16 items-center justify-center"
                initial={{ scale: 0 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
              >
                <div className="flex size-full items-center justify-center rounded-full border-2 border-blue-500/30 bg-blue-500/20 backdrop-blur-sm">
                  <Icon
                    className="text-blue-500"
                    height="1.5rem"
                    icon="lucide:image"
                    width="1.5rem"
                  />
                </div>
              </motion.div>
              <DialogTitle className="text-accent-foreground text-center text-2xl font-bold">
                {t("settings.workspaceInfo.logoDialog.title")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Upload Section */}
              <div>
                <h3 className="text-accent-foreground mb-2 text-lg font-semibold">
                  {t("settings.workspaceInfo.logoDialog.uploadSection.title")}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t("settings.workspaceInfo.logoDialog.uploadSection.description")}
                </p>

                {/* Drag and Drop Area with Current Logo Background */}
                {!selectedImage && (
                  <div className="relative mb-4">
                    {/* Current Logo Background */}
                    {logoUrl && (
                      <div className="absolute inset-0 flex justify-center">
                        <div className="border-border/30 relative h-52 w-52 overflow-hidden rounded-xl border">
                          <img
                            alt="Current workspace logo"
                            className="h-full w-full object-cover opacity-50"
                            src={logoUrl}
                          />
                        </div>
                      </div>
                    )}

                    {/* Drag and Drop Overlay */}
                    <div
                      className={`border-input hover:bg-accent/50 relative flex min-h-52 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed p-4 transition-colors ${
                        isDragging
                          ? "bg-accent/50 border-accent"
                          : logoUrl
                            ? "bg-background/80 backdrop-blur-sm"
                            : ""
                      }`}
                      role="button"
                      onClick={openFileDialog}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
                        <div
                          aria-hidden="true"
                          className="bg-background mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border"
                        >
                          <Icon className="size-4 opacity-60" icon="lucide:image-up" />
                        </div>
                        <p className="mb-1.5 text-sm font-medium">
                          {t(
                            "workspaces:settings.workspaceInfo.logoDialog.uploadSection.imageInput.dropText",
                          )}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {t("settings.workspaceInfo.logoDialog.uploadSection.fileRequirements")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cropper (only visible when new image is selected) */}
                {selectedImage && (
                  <div className="space-y-4">
                    <div className="bg-muted/5 relative h-80 overflow-hidden rounded-xl">
                      <Cropper
                        aspect={1}
                        crop={crop}
                        image={selectedImage}
                        objectFit="contain"
                        showGrid={true}
                        zoom={zoom}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>

                    {/* Zoom Slider */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-accent-foreground text-sm font-medium">
                          {t("common:zoom")}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {Math.round(zoom * 100)}%
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={3}
                        min={1}
                        step={0.1}
                        value={[zoom]}
                        onValueChange={(value) => setZoom(value[0])}
                      />
                    </div>
                  </div>
                )}

                {/* Upload Button (only visible when image is cropped and ready) */}
                {selectedImage && croppedAreaPixels && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4"
                    initial={{ opacity: 0, y: 10 }}
                  >
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                      <Button
                        className="bg-muted/20 hover:bg-muted/30 border-border/50 sm:w-auto"
                        size="sm"
                        variant="outline"
                        onClick={openFileDialog}
                      >
                        <Icon className="mr-2 h-4 w-4" icon="lucide:upload" />
                        {t("settings.workspaceInfo.logoDialog.uploadSection.uploadNewFile")}
                      </Button>
                      <Button
                        className="bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full rounded-xl"
                        disabled={isUploading}
                        onClick={handleUpload}
                      >
                        {isUploading ? (
                          <>
                            <Icon className="mr-2 h-4 w-4 animate-spin" icon="lucide:loader-2" />
                            {t("settings.workspaceInfo.logoDialog.uploading")}
                          </>
                        ) : (
                          <>
                            <Icon className="mr-2 h-4 w-4" icon="lucide:upload" />
                            {t("settings.workspaceInfo.logoDialog.uploadLogo")}
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Separator */}
              {logoUrl && <Separator className="bg-border/30" />}

              {/* Delete Section */}
              {logoUrl && !logoUrl.includes("api.dicebear.com") && (
                <div>
                  <h3 className="text-accent-foreground mb-2 text-lg font-semibold">
                    {t("settings.workspaceInfo.logoDialog.deleteSection.title")}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm">
                    {t("settings.workspaceInfo.logoDialog.deleteSection.description")}
                  </p>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        className="w-full rounded-xl"
                        disabled={isDeleting}
                        variant="destructive"
                      >
                        {isDeleting ? (
                          <>
                            <Icon className="mr-2 h-4 w-4 animate-spin" icon="lucide:loader-2" />
                            {t("settings.workspaceInfo.logoDialog.deleting")}
                          </>
                        ) : (
                          <>
                            <Icon className="mr-2 h-4 w-4" icon="lucide:trash-2" />
                            {t("settings.workspaceInfo.logoDialog.deleteSection.button")}
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("settings.workspaceInfo.logoDialog.deleteSection.confirmTitle")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("settings.workspaceInfo.logoDialog.deleteSection.confirmDescription")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t("settings.workspaceInfo.logoDialog.deleteSection.cancelButton")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={handleDelete}
                        >
                          {t("settings.workspaceInfo.logoDialog.deleteSection.confirmButton")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-center pt-6">
                <Button
                  className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground min-w-32 rounded-xl"
                  variant="ghost"
                  onClick={handleClose}
                >
                  {t("settings.workspaceInfo.logoDialog.close")}
                </Button>
              </div>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
